import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Tooltip from '~/components/CommonTooltip';
import CopyWithTooltip from '~/components/CopyWithTooltip';

import DataCenter from '../../../components/DataCenter';
import OperatingSystem from '../../../components/OperatingSystem';
import RenderCard from '../../../components/RenderCard';
import RootPassword from '../../../components/RootPassword';
import ServerSetupTooltipContent from '../../../components/ServerSetupTooltipContent';
import { DomainServiceStatus, PackageDetailsInfo } from '../../../utils/types';
import ChangeHostnameModal from '../../SelfManagedHosting/scenes/SelfManagedHostingLayout/Overview/ChangeHostnameModal';

interface AdvancedInfoProps {
  details: PackageDetailsInfo;
  hostingPlanId: string;
  osTemplateValue: string;
  domainServiceStatus?: DomainServiceStatus;
  serverStatus: string;
  hasSitesCapability?: boolean;
  accountType?: string | null;
  serverId?: string | null;
}

const AdvancedInfo = ({
  details: { hostname, dataCenter, operatingSystem },
  hostingPlanId,
  osTemplateValue,
  domainServiceStatus = {
    isDomainServiceConfigured: true,
    canAccessEmail: true,
  },
  serverStatus,
  hasSitesCapability = false,
  accountType = null,
  serverId = null,
}: AdvancedInfoProps) => {
  // Use separate translation hooks so keyPrefix applies only to the 'settings' namespace
  const { t: tSettings } = useTranslation('settings', {
    keyPrefix: 'nameServerAndIPCard.advHostingServerInfo',
  });
  const { t: tHosting } = useTranslation('hosting', {
    keyPrefix: 'hostingNova',
  });

  const navigate = useNavigate();
  const [openChangeHostnameModal, setOpenChangeHostnameModal] = useState(false);
  const isSelfManagedVPS = accountType === 'vps';
  // Hostname card is always shown; content switches based on whether hostname is set
  const needsSetup = !domainServiceStatus.isDomainServiceConfigured;
  const hasHostname = Boolean(hostname?.trim());

  const tooltipContent = (
    <ServerSetupTooltipContent
      accountType={accountType}
      hasHostname={hasHostname}
    />
  );
  const handleNavigateToServerSetup = () => {
    navigate('/setup-server', {
      state: { hostingPlanId, accountType, serverId },
    });
  };
  const mdValue = hasSitesCapability && operatingSystem ? 3 : 4;
  const isServerOnline = serverStatus === 'active';
  const handleOpenChangeHostnameModal = () => {
    setOpenChangeHostnameModal(true);
  };

  const rowStackProps = {
    direction: 'row',
    spacing: 0.5,
    alignItems: 'center',
    sx: { pb: 1 },
  } as const;

  let hostnameContent;
  if (needsSetup) {
    hostnameContent = (
      <Stack {...rowStackProps}>
        <WarningIcon color="warning" fontSize="small" />
        <Link
          component="button"
          onClick={handleNavigateToServerSetup}
          sx={{ textDecoration: 'none' }}
        >
          <Typography variant="body2">{tSettings('runServerSetup')}</Typography>
        </Link>
        <Tooltip title={tooltipContent} arrow placement="top">
          <IconButton size="small" data-testid="info-icon">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  } else if (hasHostname) {
    hostnameContent = (
      <Stack {...rowStackProps}>
        <Typography
          variant="body2"
          sx={{
            wordBreak: 'break-all',
            overflowWrap: 'anywhere',
            flexGrow: 1,
          }}
        >
          {hostname}
        </Typography>
        <CopyWithTooltip text={hostname} />
        {isSelfManagedVPS && (
          <IconButton
            size="small"
            onClick={handleOpenChangeHostnameModal}
            data-testid="edit-hostname-btn"
            disabled={!isServerOnline}
          >
            <EditIcon
              fontSize="small"
              color={isServerOnline ? 'primary' : 'disabled'}
            />
          </IconButton>
        )}
      </Stack>
    );
  } else {
    hostnameContent = (
      <Stack {...rowStackProps}>
        <Link
          component="button"
          onClick={handleOpenChangeHostnameModal}
          disabled={!isServerOnline}
          sx={{ textDecoration: 'none' }}
          data-testid="set-hostname-link"
        >
          <Typography variant="body2">
            {tHosting('hostName.clickToSet')}
          </Typography>
        </Link>
      </Stack>
    );
  }

  return (
    <Grid container spacing={2} data-testid="advanced-info-container">
      <Grid item xs={12} md={mdValue}>
        <RenderCard
          title={tHosting('hostName.title')}
          value={hostnameContent}
          tooltip={tHosting('hostName.tooltip')}
          tooltipIconSize={24}
          dataTestId="host-name-card"
        />
      </Grid>
      <Grid item xs={12} md={mdValue}>
        <DataCenter dataCenter={dataCenter} />
      </Grid>
      {hasSitesCapability && (
        <Grid item xs={12} md={mdValue}>
          <OperatingSystem os={osTemplateValue} accountType={accountType} />
        </Grid>
      )}
      <Grid item xs={12} md={mdValue}>
        <RootPassword
          hostingPlanId={hostingPlanId}
          serverStatus={serverStatus}
        />
      </Grid>
      {!needsSetup && (
        <ChangeHostnameModal
          open={openChangeHostnameModal}
          onClose={() => setOpenChangeHostnameModal(false)}
          hostingId={Number(hostingPlanId)}
          currentHostname={hostname}
          serverType={accountType}
        />
      )}
    </Grid>
  );
};

export default AdvancedInfo;
